import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import rightSmall from 'assets/right-small.svg';

import { categorizeFeedback, getCategoryColor } from 'utlis/categorizeFeedback';

import styles from './styles.module.scss';

export const CategoryFilter = ({ categoryName, onCategorySelect }) => {
  const currentRecord = useSelector(state => state.historyReducer.currentRecord);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Get review type and categorize feedback using the same logic as other components
  const reviewType = currentRecord?.extra_data?.template ?? currentRecord?.template;

  const categorizedFeedback = useMemo(
    () => {
      if (!currentRecord?.final_feedback?.length) {
        return [];
      }

      return categorizeFeedback(currentRecord.final_feedback, reviewType, currentRecord);
    },
    [currentRecord, reviewType]
  );

  // Transform categorized feedback into categories format for the component
  const categories = useMemo(() => categorizedFeedback.map(category => ({
    key: category.categoryKey,
    title: category.categoryName,
    count: category.categoryFeedback.length,
    icon: category.categoryIcon,
    severityColor: getCategoryColor(category.categoryFeedback),
    categoryFeedback: category.categoryFeedback,
  })), [categorizedFeedback]);

  // Find the selected category based on categoryName prop
  const selectedCategory = useMemo(() => {
    if (!categoryName || !categorizedFeedback.length) {
      return null;
    }

    return categorizedFeedback.find(
      category => category.categoryName === categoryName || category.categoryKey === categoryName
    );
  }, [categoryName, categorizedFeedback]);

  // Calculate findings count for the selected category, fallback to total if no selection
  const displayFindings = useMemo(() => {
    if (selectedCategory) {
      return selectedCategory.categoryFeedback.length;
    }

    return categorizedFeedback.reduce((total, category) => total + category.categoryFeedback.length, 0);
  }, [selectedCategory, categorizedFeedback]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const onCategoryClick = categoryKey => {
    onCategorySelect?.(categoryKey);
    setIsDropdownOpen(false);
  };

  return (
    <div className={styles['filter-container']} ref={dropdownRef}>
      {/* Main header with title and findings count */}
      <div className={styles['header']} onClick={toggleDropdown}>
        <h3 className={styles['title']}>
          {categoryName}
        </h3>
        <div className={styles['findings-section']}>
          <span
            className={styles['findings-count']}
            style={{ color: selectedCategory ? getCategoryColor(selectedCategory.categoryFeedback) : '#10B981' }}
          >
            {displayFindings}
          </span>
          <span
            className={styles['findings-text']}
            style={{ color: selectedCategory ? getCategoryColor(selectedCategory.categoryFeedback) : '#10B981' }}
          >
            finding
            {displayFindings === 1 ? '' : 's'}
          </span>
          <div className={cn(styles['chevron'], { [styles['chevron-open']]: isDropdownOpen })}>
            <img
              src={rightSmall}
              alt="dropdown arrow"
            />
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className={styles['dropdown']}>
          <div className={styles['section']}>
            <div className={styles['heading']}>
              <span className={styles['categories-title']}>
                Categories
              </span>
            </div>
            <div className={styles['list']}>
              {categories.map(category => (
                <button
                  key={category.key}
                  className={cn(styles['category'], {
                    [styles['category-highlighted']]: selectedCategory?.categoryKey === category.key,
                  })}
                  onClick={() => onCategoryClick(category.key)}
                >
                  <div className={styles['icon-labels']}>
                    <img src={category.icon} className={styles['category-icon']} />
                    <div className={styles['labels']}>
                      <div className={styles['category-title']}>
                        {category.title}
                      </div>
                      <div className={styles['findings']}>
                        <span
                          className={styles['findings-number']}
                          style={{ color: category.severityColor }}
                        >
                          {category.count}
                        </span>
                        <span
                          className={styles['findings-label']}
                          style={{ color: category.severityColor }}
                        >
                          finding
                          {category.count === 1 ? '' : 's'}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CategoryFilter.propTypes = {
  categoryName: PropTypes.string,
  onCategorySelect: PropTypes.func,
};

export default CategoryFilter;
